import api from '../http.service';
import { API_ENDPOINTS } from '@/config/api.config';

export interface Bot {
  id: string;
  tenant_id?: string;
  tenant_name?: string; // Populated for superadmin
  bot_id: string;
  name: string;
  description?: string;
  version: string;
  flow: any;
  status: 'draft' | 'published' | 'archived';
  is_published: boolean;
  published_at?: string;
  tags?: string[];
  channel_id?: string;
  settings?: BotSettings;
  created_by?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
  /** md5 of the stored flow; send back as expectedFlowRevision to detect concurrent saves. */
  flow_revision?: string;
}

export interface BotSettings {
  // General
  defaultLanguage?: string;
  timezone?: string;
  /** Minutes without a message before the conversation starts over; 0 = never (shared/sessionTimeout.js). */
  sessionTimeout?: number;
  /** On coming back after the timeout: forget the answers ("all") or keep them ("position"). */
  sessionReset?: 'all' | 'position';
  /** Flow to start in after the timeout (default "main"). */
  sessionReturnFlow?: string;
  /** Reminder while the bot waits for a reply (shared/sessionTimeout.js `inactivitySettings`). */
  inactivity?: {
    /** Minutes; 0 = off. */
    afterMinutes?: number;
    action?: 'message' | 'flow';
    message?: string;
    flow?: string;
  };
  /** Saved user values (`user.*`): days kept after their last change, 0 = until deleted (shared/userData.js). */
  userData?: {
    keepDays?: number;
  };
  /** Background flows by event; empty = off (shared/botHooks.js `hookSettings`). */
  hooks?: {
    incoming?: string;
    outgoing?: string;
    sessionEnd?: string;
  };
  
  // Webhooks
  webhooks?: {
    incoming?: string;
    outgoing?: string;
    events?: string;
    secret?: string;
    /** Required by io-handler on inbound webhooks (non-web channels): X-Webhook-Token, Bearer, ?token= or HMAC. */
    inboundToken?: string;
  };
  
  // AI/NLP
  ai?: {
    provider?: 'openai' | 'anthropic' | 'google' | 'custom';
    model?: string;
    temperature?: number;
    maxTokens?: number;
    systemPrompt?: string;
    topP?: number;
    frequencyPenalty?: number;
    presencePenalty?: number;
    /** Tenant-provided key for Studio AI features when env key is not used */
    apiKey?: string;
  };
  
  // Advanced
  advanced?: {
    rateLimitPerMinute?: number;
    rateLimitPerHour?: number;
    fallbackBehavior?: 'default' | 'handoff' | 'message';
    fallbackMessage?: string;
    retryAttempts?: number;
    timeout?: number;
    debugMode?: boolean;
  };
  
  // Environment Variables
  envVars?: { key: string; value: string; isSecret?: boolean }[];

  /** Bot Config → Email / SMS / Sheets (optional integrations). */
  email?: Record<string, unknown>;
  sms?: Record<string, unknown>;
  googleSheets?: unknown[];

  /** External API definitions (Bot Config → APIs); used by apiCall nodes at runtime. */
  apis?: Array<{ id: string; name: string; method?: string; [key: string]: unknown }>;
  /** Which base URL saved connections call: live (default) or test (each connection's testBaseUrl). */
  apiEnvironment?: 'live' | 'test';
}

export interface CreateBotData {
  name: string;
  description?: string;
  botId?: string;
  channelId?: string;
  tenantId?: string; // For superadmin to specify which tenant the bot belongs to
}

export interface UpdateBotData {
  name?: string;
  description?: string;
  flow?: any;
  status?: string;
  tags?: string[];
  /** Reject the save with 409 if the stored flow no longer has this revision. */
  expectedFlowRevision?: string;
  /** Label for this save in version history (e.g. "Converted from Botpress"). */
  changeSummary?: string;
}

export interface BotListResponse {
  bots: Bot[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface TestFlowStepResult {
  input: string;
  nodeVisited: string;
  response: string;
  transitionTaken: string;
  error?: string;
}

export interface TestFlowResult {
  results: TestFlowStepResult[];
  flowValid: boolean;
  deadEndPaths: string[];
}

// List bots
export const listBots = async (params?: {
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
  tenantId?: string; // For superadmin tenant filtering
}): Promise<BotListResponse> => {
  const response = await api.get(API_ENDPOINTS.STUDIO.BOTS, { params });
  return response.data.data;
};

// Get bot by ID
export const getBot = async (id: string): Promise<Bot> => {
  const response = await api.get(`${API_ENDPOINTS.STUDIO.BOTS}/${id}`);
  return response.data.data;
};

// Create bot
export const createBot = async (data: CreateBotData): Promise<Bot> => {
  const response = await api.post(API_ENDPOINTS.STUDIO.BOTS, data);
  return response.data.data;
};

/** Imported bots carry their whole flow (and legacy bundle) in one write: allow slow uplinks. */
export const LARGE_WRITE_TIMEOUT_MS = 10 * 60 * 1000;

/** Gzip a JSON body (studio-server's express.json inflates `Content-Encoding: gzip`). Null when unsupported. */
export const gzipJson = async (data: unknown): Promise<ArrayBuffer | null> => {
  if (typeof CompressionStream === 'undefined') return null;
  const stream = new Response(JSON.stringify(data)).body!.pipeThrough(new CompressionStream('gzip'));
  return new Response(stream).arrayBuffer();
};

/** Bodies above this are sent gzip-compressed with the long timeout (editor saves of large bots). */
export const LARGE_WRITE_BYTES = 256 * 1024;

const putBot = async (id: string, data: UpdateBotData, large: boolean): Promise<Bot> => {
  const body = large ? await gzipJson(data) : null;
  const config = large ? { timeout: LARGE_WRITE_TIMEOUT_MS } : undefined;
  const response = body
    ? await api.put(`${API_ENDPOINTS.STUDIO.BOTS}/${id}`, body, {
        ...config,
        headers: { 'Content-Type': 'application/json', 'Content-Encoding': 'gzip' },
      })
    : await api.put(`${API_ENDPOINTS.STUDIO.BOTS}/${id}`, data, config);
  return response.data.data;
};

// Update bot (a large flow goes gzip-compressed, with the long timeout)
export const updateBot = async (id: string, data: UpdateBotData): Promise<Bot> =>
  putBot(id, data, !!data.flow && JSON.stringify(data.flow).length > LARGE_WRITE_BYTES);

// Update bot with a large flow (bot import): always gzip-compressed, long timeout
export const updateBotLarge = async (id: string, data: UpdateBotData): Promise<Bot> => putBot(id, data, true);

// Delete bot
export const deleteBot = async (id: string): Promise<void> => {
  await api.delete(`${API_ENDPOINTS.STUDIO.BOTS}/${id}`);
};

// Publish bot
export const publishBot = async (id: string): Promise<Bot> => {
  const response = await api.post(`${API_ENDPOINTS.STUDIO.BOTS}/${id}/publish`);
  return response.data.data;
};

/** Dry-run walk of the bot flow (server-side; does not call chat-engine). */
export const testBotFlow = async (botId: string, messages: string[]): Promise<TestFlowResult> => {
  const response = await api.post(`${API_ENDPOINTS.STUDIO.BOTS}/${botId}/test-flow`, { messages });
  return response.data.data;
};

// Duplicate bot
export const duplicateBot = async (id: string, name?: string, botId?: string): Promise<Bot> => {
  const response = await api.post(`${API_ENDPOINTS.STUDIO.BOTS}/${id}/duplicate`, { name, botId });
  return response.data.data;
};

// Get bot settings
export const getBotSettings = async (id: string): Promise<BotSettings> => {
  const response = await api.get(`${API_ENDPOINTS.STUDIO.BOTS}/${id}/settings`);
  return response.data.data;
};

// Update bot settings
export const updateBotSettings = async (id: string, settings: Partial<BotSettings>): Promise<BotSettings> => {
  const response = await api.put(`${API_ENDPOINTS.STUDIO.BOTS}/${id}/settings`, settings);
  return response.data.data;
};

// Version history types
export interface BotVersion {
  id: string;
  version_number: number;
  change_summary: string | null;
  created_by: string;
  created_by_name: string;
  created_at: string;
}

// Get version history
export const getVersionHistory = async (id: string): Promise<BotVersion[]> => {
  const response = await api.get(`${API_ENDPOINTS.STUDIO.BOTS}/${id}/versions`);
  return response.data.data;
};

// Restore to a specific version
export const restoreVersion = async (id: string, versionId: string): Promise<any> => {
  const response = await api.post(`${API_ENDPOINTS.STUDIO.BOTS}/${id}/versions/${versionId}/restore`);
  return response.data.data;
};

export default {
  listBots,
  getBot,
  createBot,
  updateBot,
  updateBotLarge,
  deleteBot,
  publishBot,
  testBotFlow,
  duplicateBot,
  getBotSettings,
  updateBotSettings,
  getVersionHistory,
  restoreVersion,
};

